import { useState } from "react";
import { useParams, Link } from "react-router";
import { AlertTriangleIcon, ChevronLeftIcon, DownloadIcon } from "lucide-react";
import {
  usePeriodDetail,
  usePeriodEvents,
  periodExportUrl,
  type SavingsEvent,
} from "@/api/hooks/use-billing-statements";
import {
  useBillingDisputes,
  type BillingDispute,
} from "@/api/hooks/use-billing-disputes";
import { useLayoutContext } from "@/components/Layout";
import { DisputeModal } from "@/components/DisputeModal";
import { KpiCard } from "@/components/KpiCard";
import { KpiGrid } from "@/components/KpiGrid";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { ErrorState } from "@/components/ErrorState";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { Drawer } from "@/components/Drawer";
import { EventDetailPanel } from "@/components/EventDetailPanel";
import { formatUsd, formatTimestamp } from "@/lib/format";

// Keep in sync with settings.dispute_window_days (default 15).
const DISPUTE_WINDOW_DAYS = 15;

const PAGE_SIZE = 50;

export function StatementDetail() {
  const params = useParams<{ periodId: string }>();
  const periodId = params.periodId ?? null;
  const { tenantId } = useLayoutContext();

  const detailQ = usePeriodDetail(periodId);
  const disputesQ = useBillingDisputes(tenantId);

  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([]);
  const eventsQ = usePeriodEvents(periodId, cursor, PAGE_SIZE);
  const [selected, setSelected] = useState<SavingsEvent | null>(null);
  const [disputeOpen, setDisputeOpen] = useState(false);

  if (!periodId) {
    return <EmptyState message="Missing statement id" />;
  }

  if (detailQ.isLoading) {
    return <LoadingSkeleton cards={4} rows={6} />;
  }

  if (detailQ.isError) {
    return (
      <ErrorState
        message={detailQ.error?.message || "Failed to load statement"}
        onRetry={() => detailQ.refetch()}
      />
    );
  }

  const period = detailQ.data!;
  const feeRatePct = (parseFloat(period.savings_fee_rate) * 100).toFixed(0);

  const events = eventsQ.data?.items ?? [];
  const hasNext = !!eventsQ.data?.next_cursor;
  const hasPrev = cursorStack.length > 0;

  const periodDisputes = (disputesQ.data?.disputes ?? []).filter(
    (d) => d.billing_period_id === period.id,
  );
  const activeDispute = periodDisputes.find(
    (d) => d.status === "open" || d.status === "in_review",
  );
  const latestDispute: BillingDispute | undefined = periodDisputes[0];
  const disputeButtonVisible =
    (period.status === "finalized" || period.status === "draft") &&
    !disputeWindowExpired(period.finalized_at) &&
    !activeDispute;

  function nextPage() {
    if (!eventsQ.data?.next_cursor) return;
    setCursorStack((stack) => [...stack, cursor]);
    setCursor(eventsQ.data.next_cursor);
  }

  function prevPage() {
    setCursorStack((stack) => {
      const next = [...stack];
      const previous = next.pop() ?? null;
      setCursor(previous);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/statements"
          className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg transition-colors mb-3"
        >
          <ChevronLeftIcon size={14} />
          Back to statements
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-fg">
              {period.period_start} &mdash; {period.period_end}
            </h1>
            <p className="text-xs text-fg-muted mt-1">
              Methodology {period.methodology_version} ·{" "}
              <a
                href={period.methodology_doc_url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-electric-blue hover:underline"
              >
                How we calculate savings
              </a>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={period.status} />
            {disputeButtonVisible && (
              <button
                type="button"
                onClick={() => setDisputeOpen(true)}
                className="inline-flex items-center gap-1 rounded border border-caution-amber/40 px-2.5 py-1 text-xs text-caution-amber hover:bg-caution-amber/10 transition-colors"
              >
                <AlertTriangleIcon size={12} />
                Dispute
              </button>
            )}
            <a
              href={periodExportUrl(period.id, "csv")}
              className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1 text-xs text-fg hover:bg-surface-2 transition-colors"
            >
              <DownloadIcon size={12} />
              CSV
            </a>
            <a
              href={periodExportUrl(period.id, "json")}
              className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1 text-xs text-fg hover:bg-surface-2 transition-colors"
            >
              <DownloadIcon size={12} />
              JSON
            </a>
          </div>
        </div>
      </div>

      {latestDispute && (
        <DisputeBanner dispute={latestDispute} />
      )}

      {/* Period totals */}
      <KpiGrid>
        <KpiCard
          label="Verified Savings"
          value={formatUsd(period.total_verified_savings)}
          color="green"
          subtitle={`${period.billable_requests.toLocaleString()} billable / ${period.total_requests.toLocaleString()} total`}
        />
        <KpiCard
          label="Invoice"
          value={formatUsd(period.total_invoice_amount)}
          subtitle={`${feeRatePct}% of verified savings`}
        />
        <KpiCard
          label="Actual LLM Spend"
          value={formatUsd(period.total_actual_cost)}
          subtitle={`Baseline ${formatUsd(period.total_baseline_cost)}`}
        />
        <KpiCard
          label="Gross Savings"
          value={formatUsd(period.gross_savings)}
          subtitle="Baseline − Actual"
        />
      </KpiGrid>

      {/* Fee math breakdown */}
      <div className="rounded-lg border border-line-subtle bg-surface p-5 space-y-3">
        <h2 className="text-sm font-medium text-fg">Fee calculation</h2>
        <div className="rounded border border-line-subtle bg-canvas px-4 py-4 space-y-2">
          <FeeRow
            label="Baseline (what you would have paid)"
            value={formatUsd(period.total_baseline_cost)}
          />
          <FeeRow
            label="Actual (billed by provider)"
            value={formatUsd(period.total_actual_cost)}
            negative
          />
          <div className="border-t border-line-subtle my-1" />
          <FeeRow
            label="Gross savings (Baseline − Actual)"
            value={formatUsd(period.gross_savings)}
            highlight
          />
          <FeeRow
            label="Verified savings (after exclusions)"
            value={formatUsd(period.total_verified_savings)}
            subtle
          />
          <div className="border-t border-line-subtle my-1" />
          <FeeRow
            label={`Savings fee at ${feeRatePct}% (Verified × Fee Rate)`}
            value={formatUsd(period.savings_fee)}
          />
          <div className="border-t border-line-subtle my-1" />
          <div className="flex items-center justify-between pt-1">
            <p className="text-sm font-medium text-fg">
              Total invoice
            </p>
            <span className="text-base font-semibold text-fg font-mono">
              {formatUsd(period.total_invoice_amount)}
            </span>
          </div>
        </div>
      </div>

      {/* Events drill-down */}
      <div className="rounded-lg border border-line-subtle bg-surface">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line-subtle">
          <h2 className="text-sm font-medium text-fg">Savings events</h2>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={prevPage}
              disabled={!hasPrev}
              className="rounded border border-line px-2.5 py-1 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={nextPage}
              disabled={!hasNext}
              className="rounded border border-line px-2.5 py-1 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>

        {eventsQ.isLoading ? (
          <div className="px-4 py-6">
            <LoadingSkeleton rows={5} />
          </div>
        ) : events.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-fg-muted">
            No events on this page.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-line-subtle text-fg-muted">
                <th className="px-4 py-2 text-left font-medium">Created</th>
                <th className="px-4 py-2 text-left font-medium">Models</th>
                <th className="px-4 py-2 text-right font-medium">Baseline</th>
                <th className="px-4 py-2 text-right font-medium">Actual</th>
                <th className="px-4 py-2 text-right font-medium">Verified</th>
                <th className="px-4 py-2 text-left font-medium">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {events.map((ev) => (
                <tr
                  key={ev.id}
                  className="hover:bg-surface-2 cursor-pointer"
                  onClick={() => setSelected(ev)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelected(ev);
                    }
                  }}
                >
                  <td className="px-4 py-2 text-fg-muted font-mono whitespace-nowrap">
                    {formatTimestamp(ev.created_at)}
                  </td>
                  <td className="px-4 py-2 text-fg">
                    <span className="text-fg-muted">{ev.baseline_model}</span>
                    <span className="mx-1 text-fg-faint">→</span>
                    <span>{ev.actual_model}</span>
                  </td>
                  <td className="px-4 py-2 text-right text-fg-muted font-mono">
                    {formatUsd(ev.baseline_cost)}
                  </td>
                  <td className="px-4 py-2 text-right text-fg font-mono">
                    {formatUsd(ev.actual_cost)}
                  </td>
                  <td className="px-4 py-2 text-right text-safe-green font-mono">
                    {formatUsd(ev.verified_savings)}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={ev.confidence} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Savings event"
      >
        {selected && <EventDetailPanel event={selected} />}
      </Drawer>

      <DisputeModal
        open={disputeOpen}
        onClose={() => setDisputeOpen(false)}
        periodId={period.id}
        tenantId={tenantId}
        events={events}
        onFiled={() => {
          detailQ.refetch();
          disputesQ.refetch();
          eventsQ.refetch();
        }}
      />
    </div>
  );
}

function disputeWindowExpired(finalizedAt: string | null): boolean {
  // Draft periods (finalized_at == null) are always within the window.
  if (!finalizedAt) return false;
  const finalized = Date.parse(finalizedAt);
  if (Number.isNaN(finalized)) return false;
  const deadline = finalized + DISPUTE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() > deadline;
}

function DisputeBanner({ dispute }: { dispute: BillingDispute }) {
  const filed = dispute.created_at ? formatTimestamp(dispute.created_at) : "";
  const resolved =
    dispute.resolved_at && dispute.status.startsWith("resolved");
  const withdrawn = dispute.status === "withdrawn";

  let title: string;
  let body: string;
  if (resolved) {
    const label =
      dispute.status === "resolved_uphold" ? "upheld" : "recalculated";
    title = `Dispute ${label} ${dispute.resolved_at ? formatTimestamp(dispute.resolved_at) : ""}`;
    body = dispute.resolution_note || "Resolution details forthcoming.";
  } else if (withdrawn) {
    title = `Dispute withdrawn ${dispute.resolved_at ? formatTimestamp(dispute.resolved_at) : ""}`;
    body = dispute.resolution_note || "";
  } else {
    title = `Disputed ${filed}.`;
    body = "We'll be in touch within 5 business days.";
  }

  return (
    <div className="rounded-lg border border-caution-amber/40 bg-caution-amber/5 px-4 py-3">
      <div className="flex items-start gap-2">
        <AlertTriangleIcon size={14} className="mt-0.5 text-caution-amber shrink-0" />
        <div>
          <p className="text-sm font-medium text-fg">{title}</p>
          {body && <p className="mt-1 text-xs text-fg-muted">{body}</p>}
        </div>
      </div>
    </div>
  );
}

function FeeRow({
  label,
  value,
  highlight = false,
  negative = false,
  subtle = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  negative?: boolean;
  subtle?: boolean;
}) {
  const valueColor = highlight
    ? "text-safe-green"
    : subtle
      ? "text-fg-muted"
      : "text-fg";
  return (
    <div className="flex items-center justify-between text-xs">
      <span className={subtle ? "text-fg-muted" : "text-fg-muted"}>{label}</span>
      <span className={`font-mono ${valueColor}`}>
        {negative ? "− " : ""}
        {value}
      </span>
    </div>
  );
}
